import {
  Box,
  Text,
  VStack,
  Button,
  Progress,
  Image,
  BoxProps,
  chakra,
  HStack,
  useToast,
} from "@chakra-ui/react";
import ReactS3Uploader, { S3Response } from "react-s3-uploader";

import { useLayoutEffect, useRef, useState } from "react";
import { useLoading } from "src/Manager/hooks/form";
import KYDLoading from "src/ui/KYDLoading";
import { AddIcon } from "@chakra-ui/icons";
import {
  isDuplicateCreativeSlot,
  sha256Hex,
} from "../utils/creativeSlotDigest";

const Video = chakra("video");
interface MediaUploadAreaProps extends Omit<BoxProps, "onChange"> {
  name?: string;
  variant?: "default" | "modern";
  resolution?: string;
  ratio: string;
  url?: string;
  onClick?: () => void;
  createUploadUrl: ({
    file,
    callback,
  }: {
    file: File;
    callback: ({ signedUrl }) => void | Promise<void | string>;
  }) => Promise<{ signedUrl: string; fileKey: string }>;
  onChange: (result: {
    cdn_url: string;
    file_key: string;
    temp_url: string;
    mime_type: string;
    file: File;
    /** SHA-256 of the file chosen in this editor session. Absent on remove. */
    contentDigest?: string;
  }) => void;
  /** Digest of the other placement in this editor session. Blank means that slot has none. */
  otherSlotDigest?: string;
  showDelete?: boolean;
  noPadding?: boolean;
  uploadButton?: boolean;
  isDisabled?: boolean;
  mediaType?: "photo" | "video";
  /** Stable selector for E2E / agent-browser (set on the hidden file input). */
  fileInputTestId?: string;
}

const MediaUploadArea = ({
  name,
  variant = "default",
  mediaType = "photo",
  resolution,
  ratio,
  url,
  onClick,
  onChange,
  createUploadUrl,
  noPadding = false,
  showDelete = true,
  uploadButton = true,
  isDisabled = false,
  fileInputTestId,
  otherSlotDigest,
  ...props
}: MediaUploadAreaProps) => {
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useLoading({
    upload: false,
  });
  const uploadRef = useRef<HTMLInputElement>(null);
  const pendingDigestRef = useRef<string | undefined>(undefined);
  const selectionRef = useRef(0);
  const toast = useToast();

  useLayoutEffect(() => {
    if (!fileInputTestId) return;
    const apply = () => {
      const el = uploadRef.current;
      if (el) el.setAttribute("data-testid", fileInputTestId);
    };
    apply();
    const raf = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(raf);
  }, [fileInputTestId, url]);

  const renderNameAndRatio = () => {
    return (
      <HStack textAlign="center" justifyContent="center">
        <Text fontSize="sm" fontWeight={"bold"} textAlign={"center"}>
          {name}
        </Text>
        <Text fontSize="xs" color="gray.500" fontWeight={"medium"}>
          {variant === "modern" ? ratio.replace("/", ":") : ""}
        </Text>
      </HStack>
    );
  };

  return (
    <Box
      rounded={"xl"}
      bg={variant === "modern" ? "transparent" : "gray.200"}
      aspectRatio={ratio}
      p={url ? props.p || 0 : 2}
      border={variant === "modern" ? "none" : "1px solid"}
      borderColor={variant === "modern" ? "transparent" : "gray.300"}
      width="100%"
      height={variant === "modern" ? "100%" : "auto"}
      {...props}
    >
      <ReactS3Uploader
        style={{ display: "none" }}
        //@ts-ignore
        inputRef={uploadRef}
        getSignedUrl={(file, callback) => createUploadUrl({ file, callback })}
        accept="image/*,video/*"
        onProgress={(p) => {
          setProgress(p);
        }}
        preprocess={(f, next) => {
          const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024; // 100MB
          if (f.size > MAX_FILE_SIZE_BYTES) {
            toast({
              status: "error",
              title:
                "File size exceeds 100MB limit. Please choose a smaller file.",
            });
            return;
          }
          const selection = ++selectionRef.current;
          void (async () => {
            let digest: string;
            try {
              digest = await sha256Hex(f);
            } catch (error) {
              console.error(error);
              if (selection !== selectionRef.current) return;
              toast({
                status: "error",
                title: "Could not read this file. Please try again.",
              });
              if (uploadRef.current) uploadRef.current.value = "";
              return;
            }
            if (selection !== selectionRef.current) return;
            if (isDuplicateCreativeSlot(otherSlotDigest, digest)) {
              toast({
                status: "error",
                title:
                  "This file is already used in the other placement. Choose a different file.",
              });
              if (uploadRef.current) uploadRef.current.value = "";
              return;
            }
            pendingDigestRef.current = digest;
            setLoading("upload", true);
            next(f);
          })();
        }}
        onError={(e) => {
          console.log("onError", e);
          console.error(e);
          setLoading("upload", false);
          setProgress(0);
        }}
        onFinish={(result: S3Response & { cdn_url: string }, file) => {
          setLoading("upload", false);
          setProgress(0);
          onChange({
            cdn_url: result.cdn_url,
            file_key: result.fileKey,
            temp_url: URL.createObjectURL(file),
            mime_type: file.type,
            file,
            contentDigest: pendingDigestRef.current,
          });
          pendingDigestRef.current = undefined;

          //@ts-ignore
          if (uploadRef.current) {
            uploadRef.current.value = "";
          }
        }}
        uploadRequestHeaders={(file) => ({
          "Content-Type": file.type,
          "x-amz-acl": "private",
        })}
        contentDisposition="auto"
      />

      <VStack h="100%" w="100%">
        {!url ? (
          <VStack
            alignItems={"center"}
            justifyContent={"center"}
            spacing={8}
            rounded={"xl"}
            overflow={"hidden"}
            bg={variant === "modern" ? "transparent" : "gray.200"}
            p={noPadding ? 0 : 2}
            border={noPadding ? "none" : "1px dashed"}
            borderColor="gray.300"
            cursor={isDisabled ? "not-allowed" : "pointer"}
            onClick={() =>
              loading.upload || isDisabled ? null : uploadRef.current?.click()
            }
            opacity={loading.upload || isDisabled ? 0.5 : 1}
            h="100%"
            w="full"
          >
            <VStack textAlign={"center"}>
              {name && renderNameAndRatio()}
              {resolution && (
                <Text fontSize={"xs"} color="gray.500">
                  {resolution}
                </Text>
              )}
              {loading.upload ? (
                <VStack>
                  <KYDLoading strokeColor="#000000" />
                  <Progress colorScheme="white" value={progress} />
                </VStack>
              ) : (
                <VStack alignItems={"center"} justifyContent={"center"}>
                  <Text fontSize={"xs"} color="gray.500" flex={1} flexGrow={1}>
                    Drag & drop file here
                  </Text>
                </VStack>
              )}
            </VStack>

            {uploadButton && (
              <Button
                variant={"outline"}
                border="1px solid #E2E2E2"
                fontWeight="400"
                h="40px"
                colorScheme="gray"
                fontSize="13px"
                gap={2}
                borderRadius="lg"
                bg="white"
                dropShadow="0px 2px 6px rgba(0,0,0,0.05)"
                size={"sm"}
                disabled={loading.upload || isDisabled}
                rightIcon={<AddIcon color="#5D5D5D" />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isDisabled) return;
                  uploadRef.current?.click();
                }}
              >
                {variant === "modern" ? "Add Media" : "Upload Media"}
                {loading.upload && (
                  <Progress colorScheme="white" value={progress} />
                )}
              </Button>
            )}
          </VStack>
        ) : (
          <VStack
            justifyContent={"space-evenly"}
            border={noPadding ? "none" : "1px dashed"}
            gap={noPadding ? 0 : 2}
            borderColor="gray.300"
            h="100%"
            w="100%"
          >
            {name && renderNameAndRatio()}
            <Box
              position="relative"
              alignItems={"center"}
              justifyContent={"center"}
              rounded={variant === "modern" ? "none" : "xl"}
              overflow="hidden"
              aspectRatio={ratio}
              maxH="80%"
              w="auto"
              minW={0}
              bg={variant === "modern" ? "transparent" : "gray.200"}
              mx="auto"
              p={noPadding ? 0 : 2}
              onClick={() => {
                if (isDisabled || loading.upload) return;
                uploadRef.current?.click();
              }}
              cursor={isDisabled || loading.upload ? "not-allowed" : "pointer"}
            >
              {mediaType === "video" ? (
                <Video
                  src={url}
                  controls
                  autoPlay
                  muted
                  loop
                  width="100%"
                  height="100%"
                  objectFit="contain"
                  filter={loading.upload ? "grayscale(1) brightness(0.55)" : ""}
                />
              ) : (
                <Image
                  src={url}
                  width="100%"
                  height="100%"
                  objectFit={variant === "modern" ? "cover" : "contain"}
                  filter={loading.upload ? "grayscale(1) brightness(0.55)" : ""}
                />
              )}
              {loading.upload ? (
                <Box
                  position="absolute"
                  inset={0}
                  bg="blackAlpha.300"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  pointerEvents="all"
                >
                  <VStack spacing={3} w="70%">
                    <KYDLoading strokeColor="#FFFFFF" />
                    <Text
                      fontSize="sm"
                      fontWeight="bold"
                      color="white"
                    >{`${progress}%`}</Text>
                  </VStack>
                </Box>
              ) : null}
            </Box>
            {showDelete && (
              <HStack alignItems="center" justifyContent="center">
                <Text
                  as="button"
                  color="blue.500"
                  mr={4}
                  fontSize="sm"
                  fontWeight="semibold"
                  textDecoration="underline"
                  background="none"
                  border="none"
                  cursor="pointer"
                  _hover={{ color: "blue.700" }}
                  aria-disabled={isDisabled || loading.upload}
                  _disabled={{
                    color: "gray.400",
                    textDecoration: "none",
                    cursor: "not-allowed",
                  }}
                  onClick={() => {
                    if (isDisabled || loading.upload) return;
                    uploadRef.current?.click();
                  }}
                >
                  Replace
                </Text>
                <Text
                  as="button"
                  color="blue.500"
                  fontSize="sm"
                  fontWeight="semibold"
                  textDecoration="underline"
                  background="none"
                  border="none"
                  cursor="pointer"
                  _hover={{ color: "blue.700" }}
                  aria-disabled={isDisabled || loading.upload}
                  _disabled={{
                    color: "gray.400",
                    textDecoration: "none",
                    cursor: "not-allowed",
                  }}
                  onClick={() => {
                    if (isDisabled || loading.upload) return;
                    // A hash can still be in flight. Drop that pick so it cannot upload into the cleared slot.
                    selectionRef.current += 1;
                    pendingDigestRef.current = undefined;
                    if (uploadRef.current) uploadRef.current.value = "";
                    onChange({
                      cdn_url: "",
                      file_key: "",
                      temp_url: "",
                      mime_type: "",
                      file: null,
                    });
                  }}
                >
                  Remove
                </Text>
              </HStack>
            )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

export default MediaUploadArea;
